import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, FileText, Send } from "lucide-react";
import { cn } from "../../lib/utils";
import { getAllForms } from "../../services/consultationform";
import { assignConsultationForm } from "../../services/BookingsApi";
import { toast } from "react-toastify";

interface AssignConsultationFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    bookingId: number;
    customerId: number;
}

interface FormTemplate {
    id: number;
    title: string;
    uuid: string;
    // Add other fields if needed
}

export default function AssignConsultationFormModal({
    isOpen,
    onClose,
    bookingId,
    customerId,
}: AssignConsultationFormModalProps) {
    const [forms, setForms] = useState<FormTemplate[]>([]);
    const [selectedFormIds, setSelectedFormIds] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchForms();
            setSelectedFormIds([]);
        }
    }, [isOpen]);

    const fetchForms = async () => {
        setLoading(true);
        try {
            const response = await getAllForms();
            // Adjust based on actual API response structure (response.data or response)
            // The service logs "ALL FORMS" response.data, assuming it returns { data: FormTemplate[] } or just FormTemplate[]
            // Based on consultationform.ts: return response.data directly.
            // Usually it's response.current_page ? response.data : response
            // Let's assume response.data is the array or response is the array
            const formsList = Array.isArray(response) ? response : response.forms || [];
            setForms(formsList);
        } catch (error) {
            console.error("Failed to fetch forms", error);
            toast.error("Failed to load consultation forms");
        } finally {
            setLoading(false);
        }
    };

    const toggleForm = (id: number) => {
        setSelectedFormIds((prev) =>
            prev.includes(id) ? prev.filter((fId) => fId !== id) : [...prev, id]
        );
    };

    const handleSend = async () => {
        if (selectedFormIds.length === 0) {
            toast.error("Please select at least one form");
            return;
        }

        setSubmitting(true);
        try {
            await assignConsultationForm(bookingId, {
                customer_id: customerId,
                form_ids: selectedFormIds,
            });
            toast.success("Consultation forms assigned successfully");
            onClose();
        } catch (error) {
            console.error("Failed to assign forms", error);
            toast.error("Failed to assign forms");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[1050]"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white rounded-2xl shadow-xl z-[1060] overflow-hidden border border-gray-100"
                    >
                        <div className="flex items-center justify-between p-6 border-b border-gray-100">
                            <div>
                                <h2 className="text-xl font-bold text-[#402B69]">
                                    Send Consultation Forms
                                </h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Select forms to assign to this booking
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="w-8 h-8 rounded-full bg-gray-50 text-gray-400 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="p-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
                            {loading ? (
                                <div className="flex justify-center py-8">
                                    <div className="animate-spin w-6 h-6 border-2 border-[#885ABB] border-t-transparent rounded-full" />
                                </div>
                            ) : forms.length === 0 ? (
                                <div className="text-center py-8 text-gray-400">
                                    No consultation forms available.
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {forms.map((form) => {
                                        const isSelected = selectedFormIds.includes(form.id);
                                        return (
                                            <button
                                                key={form.id}
                                                onClick={() => toggleForm(form.id)}
                                                className={cn(
                                                    "px-4 py-2 rounded-full text-sm font-bold border transition-all flex items-center gap-2",
                                                    isSelected
                                                        ? "bg-[#885ABB] text-white border-[#885ABB] shadow-md transform scale-105"
                                                        : "bg-white text-gray-600 border-gray-200 hover:border-[#885ABB]/50 hover:bg-[#885ABB]/5"
                                                )}
                                            >
                                                <span>{form.title}</span>
                                                {isSelected && <Check size={14} strokeWidth={3} />}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50/50">
                            <button
                                onClick={onClose}
                                className="px-5 py-2.5 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-100 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSend}
                                disabled={submitting || selectedFormIds.length === 0}
                                className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-[#885ABB] hover:bg-[#7040A0] transition-all flex items-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {submitting ? (
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <Send size={16} />
                                )}
                                Send Forms
                            </button>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
